import PropTypes from "prop-types";

/**
 * RealisticTank – High-fidelity 3D industrial storage tank component.
 * Features:
 * - Single unified dynamic liquid column inside the center sight-glass window matching live %
 * - Live liquid percentage numeric readout displayed cleanly inside the tank window
 * - Dynamic color transitions:
 *   - Normal (20–80%): Vibrant Cyan/Blue
 *   - High Warning (80–90%): Amber/Orange
 *   - Critical High (>90%): Flashing Red
 *   - Low Warning (<20%): Amber Warning
 * - Liquid surface wave ripple and rising micro-bubbles
 * - Top dome status indicator LED
 */
const RealisticTank = ({
  liveValue,
  isOffline,
  statusColor,
  config = {},
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const minValue = config.minLevel ?? (config.minValue ?? 0);
  const maxValue = config.maxLevel ?? (config.maxValue ?? 100);
  const warningThreshold = config.warningThreshold ?? 80;
  const criticalThreshold = config.criticalThreshold ?? 90;

  let fillLevel = 60;
  if (liveValue !== null && liveValue !== undefined && !isNaN(Number(liveValue))) {
    const range = maxValue - minValue;
    fillLevel = range > 0 ? ((Number(liveValue) - minValue) / range) * 100 : 0;
  } else if (config.fillLevel !== undefined) {
    fillLevel = config.fillLevel;
  }
  fillLevel = Math.min(100, Math.max(0, fillLevel));

  const isLow = fillLevel < 20 && fillLevel > 0;
  const isWarning = fillLevel >= warningThreshold && fillLevel < criticalThreshold;
  const isCritical = fillLevel >= criticalThreshold;

  const liquidColor = isCritical
    ? "#ef4444"
    : isWarning
    ? "#f59e0b"
    : isLow
    ? "#f97316"
    : "#0284c7";

  const liquidGlowColor = isCritical
    ? "#f87171"
    : isWarning
    ? "#fbbf24"
    : isLow
    ? "#fb923c"
    : "#38bdf8";

  // Sight glass window bounds inside metal frame in 313x457 space
  const glassX = 125;
  const glassY = 92;
  const glassW = 64;
  const glassH = 192;

  const fillH = (fillLevel / 100) * glassH;
  const fillY = glassY + glassH - fillH;

  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/tank.png"
        alt="SCADA Realistic Tank"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 313x457 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 313 457"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id="tankSightGlassClip">
            <rect x={glassX} y={glassY} width={glassW} height={glassH} rx="12" />
          </clipPath>
          <linearGradient id="realisticLiquidGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={liquidColor} stopOpacity="0.88" />
            <stop offset="35%" stopColor={liquidGlowColor} stopOpacity="0.98" />
            <stop offset="100%" stopColor={liquidColor} stopOpacity="0.88" />
          </linearGradient>
          <filter id="liquidSurfaceGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Single Dynamic Liquid Sight Glass Column ── */}
        <g clipPath="url(#tankSightGlassClip)">
          {/* Liquid Column */}
          {fillH > 0 && (
            <>
              <rect
                x={glassX}
                y={fillY}
                width={glassW}
                height={fillH}
                fill="url(#realisticLiquidGrad)"
              />

              {/* Surface Meniscus Wave */}
              <ellipse
                cx={glassX + glassW / 2}
                cy={fillY}
                rx={glassW / 2}
                ry="4"
                fill={liquidGlowColor}
                opacity="0.9"
                filter="url(#liquidSurfaceGlow)"
              />

              {/* Animated Liquid Micro-Bubbles */}
              {!isOffline && (
                <g>
                  <circle
                    cx={glassX + 16}
                    cy={glassY + glassH - 12}
                    r="2"
                    fill="#ffffff"
                    opacity="0.7"
                    style={{ animation: "scada-bubble-rise 2.4s infinite ease-in" }}
                  />
                  <circle
                    cx={glassX + 32}
                    cy={glassY + glassH - 8}
                    r="2.5"
                    fill="#ffffff"
                    opacity="0.75"
                    style={{ animation: "scada-bubble-rise 1.8s infinite 0.6s ease-in" }}
                  />
                  <circle
                    cx={glassX + 46}
                    cy={glassY + glassH - 15}
                    r="1.8"
                    fill="#ffffff"
                    opacity="0.65"
                    style={{ animation: "scada-bubble-rise 2.8s infinite 1.2s ease-in" }}
                  />
                </g>
              )}
            </>
          )}

          {/* Glass Measurement Level Ticks */}
          <g opacity="0.4">
            <line x1={glassX + 3} y1={glassY + 38} x2={glassX + 10} y2={glassY + 38} stroke="#ffffff" strokeWidth="1.2" />
            <line x1={glassX + 3} y1={glassY + 76} x2={glassX + 14} y2={glassY + 76} stroke="#ffffff" strokeWidth="1.5" />
            <line x1={glassX + 3} y1={glassY + 114} x2={glassX + 10} y2={glassY + 114} stroke="#ffffff" strokeWidth="1.2" />
            <line x1={glassX + 3} y1={glassY + 152} x2={glassX + 14} y2={glassY + 152} stroke="#ffffff" strokeWidth="1.5" />
          </g>

          {/* Glass Specular Reflection Highlight */}
          <line
            x1={glassX + 7}
            y1={glassY + 8}
            x2={glassX + 7}
            y2={glassY + glassH - 8}
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.35"
          />

          {/* ── Live Percentage Readout inside Sight Glass ── */}
          <g>
            <rect
              x={glassX + 10}
              y={glassY + glassH / 2 - 11}
              width={glassW - 20}
              height="20"
              rx="4"
              fill="rgba(15, 23, 42, 0.8)"
              stroke="rgba(255, 255, 255, 0.25)"
              strokeWidth="0.8"
            />
            <text
              x={glassX + glassW / 2}
              y={glassY + glassH / 2 + 3.5}
              fill="#ffffff"
              fontSize="11"
              fontWeight="bold"
              fontFamily="monospace"
              textAnchor="middle"
            >
              {Math.round(fillLevel)}%
            </text>
          </g>
        </g>

        {/* ── Top Dome Status LED ── */}
        <circle
          cx="157"
          cy="25"
          r="4.5"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.2"
          style={{
            animation: !isOffline ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticTank.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  config: PropTypes.object,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticTank;
